import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import { Button, Form, Spin, Modal, notification, Space, message } from "antd";
import { ExclamationCircleOutlined, RightOutlined, SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import AuthenticatedPopupLayout from "../../layouts/authenticated-popup";
import FlexCard from "../../components/flex-card";
import useDispatchWithResult from "../../hooks/useDispatchWithResult";
import { enviarAssinatura, fetchSignatarios, addPendingSigner, fetchSignatariosEmpresa } from "../../store/assinatura";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import useLoading from "../../hooks/useLoading";
import SignatariosList from "./_SignatariosList";
import { formatCPF } from "@util/cpf";
import ModalSignatario from "@/components/modal-signatario";
import SignatariosDrawer from "./_SignatariosDrawer";
import moment from "moment";

export default function CriarAssinaturaPage() {
  const loadingEnviar = useLoading(enviarAssinatura.typePrefix);
  const { t } = useTranslation();
  const location = useLocation();
  const [form] = Form.useForm();
  const dispatch = useDispatchWithResult();

  const [anexo, setAnexo] = useState(null);
  const [modalsState, setModalsState] = useState({
    cadastrados: false,
    empresas: false,
    drawer: false,
  });

  const params = new URLSearchParams(location.search);
  const empresaId = params.get("empresaId");

  const { signatariosPending } = useSelector((state) => state.assinaturas);

  const handleAddSignerFromObject = (assinante) => {
    const payload = {
      ...assinante,
      registered: Boolean(assinante.registered),
    };

    const assinantes = form.getFieldValue("assinantes") || [];
    const exists = assinantes.some((item) => item.id === payload.id);

    if (exists) {
      message.error(t("Signatário já está na lista"));
      return;
    }

    dispatch(addPendingSigner(payload));
    form.setFieldsValue({
      assinantes: [...assinantes, { ...payload, cpf: formatCPF(payload.cpf) }],
    });
    message.success(t("Signatário incluído!"));
  };

 const onFinish = (values) => {
  if (!values || !values?.assinantes || values?.assinantes?.length === 0) {
    notification.warning({
      message: "Erro ao enviar assinatura",
      description: "É preciso adicionar ao menos um signatário na lista!",
      duration: 5,
    });
  } else {
    const signersList = values.assinantes.map((assinante) => ({
      name: assinante.nome,
      email: assinante.email ?? "",
      cpf: assinante.cpf?.replace(/\D/g, "") ?? "",
      tipoAssinatura: assinante.tipoAssinatura,
      add_signatario: assinante.add_signatario ?? false,
    }));


    const validade = moment().add(30, "days").format("DD/MM/YYYY - HH:mm:ss");

    Modal.confirm({
      title: "Confirma envio deste documento para os signatários?",
      icon: <ExclamationCircleOutlined style={{ color: "#faad14" }} />,
      content: (
        <div>
          <p>
            Este envelope terá validade até <b>{validade}</b>.
          </p>
        </div>
      ),
      onOk: () =>
        dispatch(enviarAssinatura({ anexo, signers: signersList }))
          .then(() => {
            Modal.success({
              title: "Documento enviado para assinatura",
              content:
                "Para visualizar sua lista de assinaturas, vá até o menu principal em Assinaturas.",
              centered: true,
              onOk: () => window.close(),
            });
          })
          .catch((e) => {
            notification.error({
              message: "Erro ao enviar assinatura",
              description:
                e?.mensagem ||
                "Ocorreu um erro ao enviar a assinatura. Tente novamente.",
              duration: 5,
            });
          }),
      centered: true,
      okText: t("Confirmar"),
      cancelText: t("Cancelar"),
    });
  }
};

  const toggleModal = (modalName, isOpen) => {
    setModalsState((prev) => ({ ...prev, [modalName]: isOpen }));
  };

  useEffect(() => {
    const anexoRecebido = params.get("anexo");

    dispatch(fetchSignatarios());
    dispatch(fetchSignatariosEmpresa({ empresaId }));
    setAnexo(anexoRecebido);
  }, []);

  return (
    <AuthenticatedPopupLayout>
      <FlexCard style={styles.flexCard}>
        <div style={{ marginBottom: "25px" }}>
          <h1 style={{ fontSize: "20px", marginBottom: "4px" }}>
            Assistente de assinatura para o documento{" "} 
            <span style={{ fontSize: "20px", fontWeight: "bold" }}>
              &quot;{anexo ?  anexo.split("/").pop() : ""}&quot;
           </span>
          </h1>
          <h2 style={{ fontSize: "14px", fontWeight: "normal", color: "#888" }}>Preencha o conteúdo dos campos com as informações necessárias dos signatários:</h2>
        </div>
        <Space style={{ marginBottom: 25, flexWrap: "wrap" }}>
          <Button onClick={() => toggleModal("cadastrados", true)} type="primary" icon={<SearchOutlined />}>
            {t("Signatário Cadastrado")}
          </Button>
          <Button onClick={() => toggleModal("empresas", true)} type="primary" icon={<SearchOutlined />}>
            {t("Signatário da Empresa")}
          </Button>
          <Button onClick={() => toggleModal("drawer", true)}>{t("Mostrar todos os Signatários")}</Button>
        </Space>

        <ModalSignatario title="Buscar signatários cadastrados" isModalOpen={modalsState.cadastrados} setIsModalOpen={() => toggleModal("cadastrados", !modalsState.cadastrados)} handleCardClick={handleAddSignerFromObject} />
        <ModalSignatario title="Buscar signatários na empresa" isModalOpen={modalsState.empresas} empresaId={empresaId} setIsModalOpen={() => toggleModal("empresas", !modalsState.empresas)} handleCardClick={handleAddSignerFromObject} />

        <SignatariosDrawer isOpen={modalsState.drawer} onClose={() => toggleModal("drawer", false)} empresaId={empresaId} onAddSigner={handleAddSignerFromObject} signatariosPending={signatariosPending} />

        <Spin spinning={loadingEnviar}>
          <Form form={form} layout="vertical" onFinish={onFinish} autoComplete="off">
            <SignatariosList form={form} />
          </Form>
        </Spin>

        <div style={styles.confirmButtonContainer}>
          <Button type="primary" icon={<RightOutlined />} onClick={() => form.submit()}>
            {t("Encaminhar documento para assinatura")}
          </Button>
        </div>
      </FlexCard>
    </AuthenticatedPopupLayout>
  );
}

const styles = {
  flexCard: {
    position: "relative",
    paddingBottom: 80,
  },
  confirmButtonContainer: {
    position: "absolute",
    bottom: 20,
    right: 20,
  },
};
